// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The board, and the film of the last five seconds.
//
// ── THE ONE THING THIS FILE HAS TO GET RIGHT ────────────────────────────────────────────────────
// A replay where the player cannot tell WHY they died is a bad game, and it is the failure this
// genre falls into most easily: units move, somebody drops, and the loser learns nothing. So the
// renderer's real job is not the units — it is the WEDGES. Every watching unit's covered ground is
// drawn as hatching bounded by a bright line, clipped against the walls by the same visibility table
// the rules use, so "the ground he was covering" is a thing you can see rather than infer. During
// planning it updates live under a moving thumb, which is what turns "I walked into a sight line"
// from a post-mortem into a decision.
//
// Colour is never the only channel: each unit type also carries a distinct glyph, and the two sides
// differ in fill AND outline. Every colour comes from palette.ts, where tests/contrast.test.ts can
// measure it — there is not one hex literal in this file, and a test asserts that.

import { H, N, RAIL, W, WALL, canSeeTile, idx, mastField, tx, ty, type GameMap } from './map';
import { COLORS, SEAT, SURFACES } from './palette';
import { DIRS, inWedge, type MatchState, type Replay, type Side, type UnitState } from './rules';
import { MAX_WAYPOINTS, UNITS } from './tuning';
import type { SideOrders } from './orders';

export interface View {
  /** Board origin and cell size in CSS pixels. */
  x: number;
  y: number;
  cell: number;
}

/** Fit the 12x16 board into the canvas, centred, with whole-pixel cells so nothing shimmers. */
export function layout(wpx: number, hpx: number): View {
  const cell = Math.max(8, Math.floor(Math.min(wpx / W, hpx / H)));
  return { x: Math.round((wpx - cell * W) / 2), y: Math.round((hpx - cell * H) / 2), cell };
}

export const tileAt = (v: View, px: number, py: number): number => {
  const x = Math.floor((px - v.x) / v.cell);
  const y = Math.floor((py - v.y) / v.cell);
  if (x < 0 || y < 0 || x >= W || y >= H) return -1;
  return idx(x, y);
};

const cx = (v: View, x: number): number => v.x + x * v.cell;
const cy = (v: View, y: number): number => v.y + y * v.cell;

export interface Frame {
  /** Positions, facings and liveness to draw — either the planning state or a replay tick. */
  units: Array<{ x: number; y: number; face: number; alive: boolean; side: Side; n: number }>;
  masts: number[];
  hold: [number, number];
  /** Which side's eyes we are drawing through — Ambush hides what this side cannot see. */
  eye: Side;
  /** Per-unit visibility for this side, or null when everything is visible. */
  vis: Uint8Array | null;
  /** Shots to draw this frame, as [from, to] unit indices. */
  shots: Array<[number, number]>;
  /** Wedges to draw: unit indices whose covered ground should be shaded. */
  wedges: number[];
  selected: number;
  /** The route being drawn or ordered, per unit index. */
  paths: Map<number, number[]>;
}

/**
 * The ground a unit is covering: in range, inside its wedge, and not behind a wall.
 *
 * Computed from the same visibility table and the same `inWedge` the resolver uses, so what is
 * shaded is exactly what can be shot. A threat overlay that disagrees with the rules by a single
 * tile is worse than no overlay at all, because the player will trust it.
 */
export function coveredTiles(map: GameMap, u: { x: number; y: number; face: number; n: number }): number[] {
  const spec = UNITS[u.n % UNITS.length];
  const from = idx(Math.floor(u.x), Math.floor(u.y));
  const out: number[] = [];
  const r2 = spec.range * spec.range;
  for (let t = 0; t < N; t++) {
    if (map.cells[t] === WALL) continue;
    const dx = tx(t) + 0.5 - u.x;
    const dy = ty(t) + 0.5 - u.y;
    if (dx * dx + dy * dy > r2) continue;
    if (!inWedge(dx, dy, u.face, spec.wedge)) continue;
    if (!canSeeTile(map, from, t)) continue;
    out.push(t);
  }
  return out;
}

export function draw(
  ctx: CanvasRenderingContext2D,
  v: View,
  map: GameMap,
  f: Frame,
  opts: { threat: boolean; reduced: boolean },
): void {
  const c = v.cell;
  ctx.save();
  ctx.fillStyle = SURFACES.void;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

  // ── ground ────────────────────────────────────────────────────────────────────────────────────
  ctx.fillStyle = SURFACES.floor;
  ctx.fillRect(cx(v, 0), cy(v, 0), c * W, c * H);

  // ── the mast field, under everything, so it reads as ground rather than an object ─────────────
  for (const mast of f.masts) {
    ctx.fillStyle = COLORS.mast;
    ctx.globalAlpha = 0.16;
    for (const t of mastField(mast)) ctx.fillRect(cx(v, tx(t)), cy(v, ty(t)), c, c);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = COLORS.mast;
    ctx.lineWidth = Math.max(2, c * 0.09);
    ctx.strokeRect(cx(v, tx(mast)) + 1, cy(v, ty(mast)) + 1, c * 2 - 2, c * 2 - 2);
  }

  // ── terrain ───────────────────────────────────────────────────────────────────────────────────
  for (let t = 0; t < N; t++) {
    const x = cx(v, tx(t));
    const y = cy(v, ty(t));
    if (map.cells[t] === WALL) {
      // Inset, so a run of walls reads as blocks with grout between them rather than as one solid
      // field. On a board where more than a third of the tiles are solid, that seam is the whole
      // difference between "walls on a floor" and "floor-shaped holes in a wall".
      ctx.fillStyle = COLORS.wall;
      ctx.fillRect(x + 0.5, y + 0.5, c - 1, c - 1);
    } else if (map.cells[t] === RAIL) {
      // A rail stops feet and not sight, so it is drawn as a low bar with clear space above it —
      // the picture has to say "you can see over this" without a legend.
      ctx.fillStyle = COLORS.rail;
      ctx.fillRect(x + c * 0.08, y + c * 0.34, c * 0.84, c * 0.32);
    }
  }

  // ── the wedges: the whole point of the picture ────────────────────────────────────────────────
  if (opts.threat) {
    for (const ui of f.wedges) {
      const u = f.units[ui];
      if (!u.alive) continue;
      if (f.vis && !f.vis[ui]) continue;
      const tiles = coveredTiles(map, u);
      ctx.fillStyle = u.side === f.eye ? COLORS.watch : COLORS.foe;
      ctx.globalAlpha = 0.13;
      for (const t of tiles) ctx.fillRect(cx(v, tx(t)), cy(v, ty(t)), c, c);
      ctx.globalAlpha = 1;
      // The bright boundary is what actually carries the meaning — a wash light enough to read as
      // "covered" measured within 1.3:1 of the floor, so contrast cannot live in the fill.
      ctx.strokeStyle = u.side === f.eye ? COLORS.watch : COLORS.foe;
      ctx.lineWidth = Math.max(1.5, c * 0.07);
      ctx.beginPath();
      const set = new Set(tiles);
      for (const t of tiles) {
        const x = cx(v, tx(t));
        const y = cy(v, ty(t));
        if (!set.has(t - W) || ty(t) === 0) {
          ctx.moveTo(x, y);
          ctx.lineTo(x + c, y);
        }
        if (!set.has(t + W) || ty(t) === H - 1) {
          ctx.moveTo(x, y + c);
          ctx.lineTo(x + c, y + c);
        }
        if (!set.has(t - 1) || tx(t) === 0) {
          ctx.moveTo(x, y);
          ctx.lineTo(x, y + c);
        }
        if (!set.has(t + 1) || tx(t) === W - 1) {
          ctx.moveTo(x + c, y);
          ctx.lineTo(x + c, y + c);
        }
      }
      ctx.stroke();
    }
  }

  // ── ordered routes, with second-marks ─────────────────────────────────────────────────────────
  for (const [ui, path] of f.paths) {
    const u = f.units[ui];
    if (!u || !u.alive || !path.length) continue;
    ctx.strokeStyle = COLORS.path;
    ctx.lineWidth = Math.max(3, c * 0.14);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx(v, u.x), cy(v, u.y));
    for (const t of path) ctx.lineTo(cx(v, tx(t) + 0.5), cy(v, ty(t) + 0.5));
    ctx.stroke();
    // A dot per second along the route. This is the ruler that makes "arrive AFTER their watch
    // sweeps past" a thing a thumb can aim at rather than a thing a manual describes.
    const spec = UNITS[u.n % UNITS.length];
    marks(ctx, v, u, path, spec.speed).forEach((p) => {
      ctx.fillStyle = COLORS.path;
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(3, c * 0.16), 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = COLORS.tick;
      ctx.font = `700 ${Math.max(8, Math.floor(c * 0.4))}px ui-sans-serif, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(p.s), p.x, p.y + 0.5);
    });
  }

  // ── shots ─────────────────────────────────────────────────────────────────────────────────────
  for (const [a, b] of f.shots) {
    const ua = f.units[a];
    const ub = f.units[b];
    if (!ua || !ub) continue;
    ctx.strokeStyle = COLORS.shot;
    ctx.lineWidth = Math.max(2, c * 0.1);
    ctx.beginPath();
    ctx.moveTo(cx(v, ua.x), cy(v, ua.y));
    ctx.lineTo(cx(v, ub.x), cy(v, ub.y));
    ctx.stroke();
  }

  // ── units ─────────────────────────────────────────────────────────────────────────────────────
  f.units.forEach((u, i) => {
    if (f.vis && !f.vis[i]) return;
    const px = cx(v, u.x);
    const py = cy(v, u.y);
    const r = c * 0.36;
    const spec = UNITS[u.n % UNITS.length];

    if (!u.alive) {
      ctx.strokeStyle = COLORS.down;
      ctx.lineWidth = Math.max(2, c * 0.1);
      ctx.beginPath();
      ctx.moveTo(px - r, py - r);
      ctx.lineTo(px + r, py + r);
      ctx.moveTo(px + r, py - r);
      ctx.lineTo(px - r, py + r);
      ctx.stroke();
      return;
    }

    // Which way it is watching, drawn as a stub from the chip: the single most important fact about
    // an enemy unit, and the thing the whole mind-game is about.
    const d = DIRS[u.face];
    ctx.strokeStyle = COLORS[SEAT[u.side]];
    ctx.lineWidth = Math.max(2, c * 0.12);
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(px + (d[0] / 1024) * c * 0.85, py + (d[1] / 1024) * c * 0.85);
    ctx.stroke();

    ctx.fillStyle = COLORS[SEAT[u.side]];
    ctx.beginPath();
    ctx.arc(px, py, r, 0, Math.PI * 2);
    ctx.fill();
    if (i === f.selected) {
      ctx.strokeStyle = COLORS.select;
      ctx.lineWidth = Math.max(2, c * 0.1);
      ctx.beginPath();
      ctx.arc(px, py, r + c * 0.12, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.fillStyle = SURFACES.void;
    ctx.font = `700 ${Math.max(9, Math.floor(c * 0.5))}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(spec.glyph, px, py + 1);
  });

  ctx.restore();
  void opts.reduced;
}

/** Where a unit is after each whole second of the window — the marks drawn along its route. */
export function marks(
  ctx: CanvasRenderingContext2D | null,
  v: View,
  u: { x: number; y: number },
  path: number[],
  speed: number,
): Array<{ x: number; y: number; s: number }> {
  void ctx;
  const out: Array<{ x: number; y: number; s: number }> = [];
  let px = u.x;
  let py = u.y;
  let leg = 0;
  for (let s = 1; s <= 5; s++) {
    let budget = speed;
    while (budget > 1e-9 && leg < path.length) {
      const gx = tx(path[leg]) + 0.5;
      const gy = ty(path[leg]) + 0.5;
      const dx = gx - px;
      const dy = gy - py;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d <= budget) {
        px = gx;
        py = gy;
        budget -= d;
        leg++;
      } else {
        px += (dx / d) * budget;
        py += (dy / d) * budget;
        budget = 0;
      }
    }
    if (leg >= path.length && budget > 0) {
      // Arrived early: mark the arrival second and stop, rather than stacking five dots on one tile.
      out.push({ x: cx(v, px), y: cy(v, py), s });
      break;
    }
    out.push({ x: cx(v, px), y: cy(v, py), s });
  }
  return out;
}

/** A frame of the planning board, before anybody has committed. */
export function planFrame(
  s: MatchState,
  eye: Side,
  orders: SideOrders,
  selected: number,
  threatFor: 'mine' | 'theirs' | 'both',
): Frame {
  const paths = new Map<number, number[]>();
  s.units.forEach((u, i) => {
    if (u.side !== eye) return;
    const o = orders[u.n];
    if (o?.path.length) paths.set(i, o.path);
  });
  const wedges: number[] = [];
  s.units.forEach((u, i) => {
    if (!u.alive) return;
    const mine = u.side === eye;
    if (threatFor === 'both' || (threatFor === 'mine') === mine) wedges.push(i);
  });
  return {
    units: s.units.map((u) => ({ x: u.x, y: u.y, face: u.face, alive: u.alive, side: u.side, n: u.n })),
    masts: [],
    hold: [s.hold[0], s.hold[1]],
    eye,
    vis: null,
    shots: [],
    wedges,
    selected,
    paths,
  };
}

/** A frame of the replay at tick `t`. */
export function replayFrame(rep: Replay, t: number, eye: Side, fog: boolean, units: UnitState[]): Frame {
  const n = rep.n;
  const at = Math.max(0, Math.min(rep.ticks - 1, t));
  const vis = fog ? rep.vis.subarray((eye * rep.ticks + at) * n, (eye * rep.ticks + at) * n + n) : null;
  const shots: Array<[number, number]> = [];
  for (const e of rep.events) {
    // A tracer lingers a few ticks so a shot in a fast replay is not a single missed frame.
    if (e.kind === 'fire' && e.tick <= at && e.tick > at - 4) shots.push([e.u, e.v]);
  }
  return {
    units: Array.from({ length: n }, (_, u) => ({
      x: rep.pos[(at * n + u) * 2],
      y: rep.pos[(at * n + u) * 2 + 1],
      face: rep.face[at * n + u],
      alive: rep.alive[at * n + u] === 1,
      side: units[u].side,
      n: units[u].n,
    })),
    masts: rep.masts,
    hold: [rep.hold[at * 2], rep.hold[at * 2 + 1]],
    eye,
    vis,
    shots,
    wedges: [],
    selected: -1,
    paths: new Map(),
  };
}

export { MAX_WAYPOINTS };
